import React from 'react';
import { paceStatus } from '../../utils/entitlement-helpers';
import { fmtUsd } from '../../utils/format-cost';

export const MONO = { fontFamily: 'var(--font-mono)' };
export const TIER_COLOR = { code: 'var(--theme-navy)', cowork: 'var(--theme-steel)', chat: 'var(--theme-blue)' };
const DEVIATION_HIGH = 1.05;
const DEVIATION_LOW = 0.95;

export const PAGE_COLUMN = { display: 'flex', flexDirection: 'column', gap: 16 };

export const plural = (n, one, many) => (Number(n) === 1 ? one : many);

export const usdOrDash = (v) => (v == null ? '—' : fmtUsd(v));

export const pct = (v) => (v == null ? '—' : `${Math.round(v).toLocaleString()}%`);

export const count = (n) => Number(n || 0).toLocaleString();

export function toBreakdownRows(burnUp) {
  return (burnUp || []).filter((p) => p.daily > 0).map((p) => ({ DATE: p.date, SPEND: p.daily }));
}

export const asRows = (list, field) => (list || []).map((r) => ({ date: r.date, key: r[field], spend: r.spend }));

export function deviationLabel(value, median) {
  if (value == null || median == null || !(median > 0)) return null;
  const x = value / median;
  if (x >= DEVIATION_HIGH) return { text: `${x.toFixed(2)}× median`, color: 'var(--theme-coral)', bg: 'var(--theme-coralLight)' };
  if (x <= DEVIATION_LOW) return { text: `${x.toFixed(2)}× median`, color: 'var(--theme-green)', bg: 'var(--theme-greenLight)' };
  return { text: 'at median', color: 'var(--theme-textSecondary)', bg: 'var(--theme-barTrack)' };
}

export function pacedStatus(pct, elapsed) {
  if (pct == null || !(elapsed > 0)) return null;
  return paceStatus(pct / 100 / elapsed);
}

export const refreshingStyle = (refreshing) => ({
  ...PAGE_COLUMN, opacity: refreshing ? 0.6 : 1, transition: 'opacity 0.2s ease',
});

export function throughTitle(base, win) {
  return win?.isCurrentMonth && win.cutoffLabel ? `${base} Through ${win.cutoffLabel}` : base;
}

export function WfmSubtitle({ children, mono = false }) {
  return <div style={{ fontSize: 14, color: 'var(--theme-textTertiary)', ...(mono ? MONO : null) }}>{children}</div>;
}

export function layerSubtitle(layer, people) {
  const n = Number(people || 0);
  return `${layer} · ${count(n)} ${plural(n, 'person', 'people')}`;
}

export function coverageSubtitle(counts) {
  const unit = (n, one, many) => `${count(n)} ${plural(n, one, many)}`;
  return [
    unit(counts?.team, 'team', 'teams'),
    unit(counts?.release_train, 'train', 'trains'),
    unit(counts?.portfolio, 'portfolio', 'portfolios'),
  ].join(' · ');
}

export function WfmChip({ color, bg, title, children }) {
  return (
    <span
      title={title}
      style={{
        ...MONO, display: 'inline-block', padding: '2px 8px', borderRadius: 5, fontSize: 12, fontWeight: 600,
        color, background: bg, whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

export function WfmCellBar({ fraction }) {
  const f = Math.min(1, Math.max(0, Number(fraction) || 0));
  return (
    <span style={{ display: 'inline-block', width: 72, height: 8, borderRadius: 4, background: 'var(--theme-barTrack)', overflow: 'hidden' }}>
      <span style={{ display: 'block', height: '100%', width: `${f * 100}%`, background: 'var(--theme-navy)', borderRadius: 4 }} />
    </span>
  );
}

export function WfmDeviationChip({ value, median }) {
  const d = deviationLabel(value, median);
  if (!d) return <span style={{ ...MONO, color: 'var(--theme-textTertiary)' }}>—</span>;
  return <WfmChip color={d.color} bg={d.bg}>{d.text}</WfmChip>;
}
